import {useCallback, useEffect, useMemo, useRef, useState, type ComponentType} from 'react';
import {Link, useParams, useNavigate, useSearchParams} from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Save as SaveIcon,
  Rocket,
  RotateCcw,
  ChevronDown,
  Wrench,
  Loader2,
  Check,
  AlertCircle,
  AlertTriangle,
  Info,
  LayoutTemplate,
  History,
  CloudOff,
  FileQuestion,
  RefreshCw,
  Palette,
  LifeBuoy,
  KeyRound,
  Package,
  ClipboardList,
  ReceiptText,
  Send,
  Copy,
  FilePlus,
  MapPin,
  HelpCircle,
  Link2,
  Unlink,
  ExternalLink,
  FolderOpen,
  type LucideIcon,
} from 'lucide-react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {cn} from '@common-web/lib/utils';
import {DetailHeader} from '@internal/components/DetailHeader';
import type {E123Config, GroupConfig} from '@common/enrollmentSite/types';
import {groupRequiresAccessCode} from '@common/enrollmentSite/types';
import {retargetSiteLinks} from '@common/enrollmentSite/siteLinks';
import {PAY_PERIODS, sitePayPeriod} from '@common/enrollmentSite/payPeriod';
import {collectPublishBlockers, collectPublishWarnings} from '@common/enrollmentSite/publishValidation';
import {diffGroupConfigs, type ConfigChange} from '@common/enrollmentSite/configDiff';
import {broadcastPreviewSaved} from '@client/pages/public/enroll/enrollPreview';
import {ConfigDiffList} from './builder/ConfigDiffList';
import {VersionHistoryDialog} from './builder/VersionHistoryDialog';
import type {SectionProps} from './builder/controls';
import {BrandSection} from './builder/sections/BrandSection';
import {SupportSection} from './builder/sections/SupportSection';
import {LandingSection} from './builder/sections/LandingSection';
import {GateSection} from './builder/sections/GateSection';
import {CheckoutSection} from './builder/sections/CheckoutSection';
import {ConfirmationSection} from './builder/sections/ConfirmationSection';
import {FaqSection} from './builder/sections/FaqSection';
import {ResourcesSection} from './builder/sections/ResourcesSection';
import {ProductsSection} from './builder/sections/ProductsSection';
import {E123Section} from './builder/sections/E123Section';
import {TemplatePickerDialog} from './TemplatePickerDialog';
import {MirrorDialog} from './builder/MirrorDialog';
import {MirrorProvider, sectionMirrorState, type MirrorState} from './builder/mirrorLock';
import {toast} from '@common-web/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@common-web/components/ui/dropdown-menu';

interface SectionDef {
  id: string;
  label: string;
  description: string;
  tag: string;
  icon: LucideIcon;
  component?: ComponentType<SectionProps>;
  placeholder?: string;
}

const SECTIONS: SectionDef[] = [
  {id: 'brand', label: 'Brand & identity', description: 'Name, logo, color, and URL.', tag: 'global', icon: Palette, component: BrandSection},
  {id: 'support', label: 'Support & coverage', description: 'Contact details and coverage period.', tag: 'global', icon: LifeBuoy, component: SupportSection},
  {id: 'landing', label: 'Landing & hero', description: 'Hero copy and coverage cards.', tag: 'landing', icon: LayoutTemplate, component: LandingSection},
  {id: 'resources', label: 'Resources', description: 'Landing cards, plan resources, and notices.', tag: 'resources', icon: FolderOpen, component: ResourcesSection},
  {id: 'gate', label: 'Access-code gate', description: 'Entry screen before the flow.', tag: 'gate screen', icon: KeyRound, component: GateSection},
  {id: 'products', label: 'Products & plans', description: 'Plans, pricing, and steps.', tag: 'stepper', icon: Package, component: ProductsSection},
  {id: 'checkout', label: 'Checkout', description: 'Member-info form options.', tag: 'checkout', icon: ClipboardList, component: CheckoutSection},
  {id: 'confirmation', label: 'Confirmation', description: 'Receipt copy and FAQ.', tag: 'receipt', icon: ReceiptText, component: ConfirmationSection},
  {id: 'faq', label: 'FAQ page', description: 'Help-center questions and answers.', tag: 'faq page', icon: HelpCircle, component: FaqSection},
  {id: 'e123', label: 'Submission mapping', description: 'e123 PDIDs, period, and agent.', tag: 'e123', icon: Send, component: E123Section},
];

// Kept in sync with the sites list (EnrollmentSitesList) so a status reads the
// same everywhere. No NOT_STARTED here — the builder only opens draft+ sites.
const STATUS_LABEL = {DRAFT: 'Draft', LIVE: 'Live', LIVE_EDITS: 'Live · edits'} as const;
const STATUS_VARIANT = {DRAFT: 'info', LIVE: 'success', LIVE_EDITS: 'warning'} as const;

function formatSavedAt(iso: string | null): string {
  if (!iso) return 'Saved';
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'});
  if (d.toDateString() === now.toDateString()) return `Saved at ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const date = d.toLocaleDateString(undefined, sameYear ? {month: 'short', day: 'numeric'} : {month: 'short', day: 'numeric', year: 'numeric'});
  return `Saved ${date} at ${time}`;
}

export function EnrollmentSiteBuilder() {
  const {groupSlug = ''} = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const canManage = useHasPermission(CODE_PERMISSIONS.enrollment.manage.key);

  // "Build" from the list lands here unsaved: no row yet, slug + name seeded
  // from the employer, held pending until the first save creates the draft.
  const isNew = groupSlug === 'new';
  const seedName = searchParams.get('name') ?? '';
  const seedSlug = searchParams.get('slug') ?? '';
  const seedEmployerKey = searchParams.get('employer') ?? undefined;
  const validSeedSlug = /^[a-z0-9-]{3,}$/.test(seedSlug) ? seedSlug : 'new-site';

  const {data, isLoading, error, refetch, isFetching} = trpc.internal.enrollmentSite.get.useQuery(
    {groupSlug},
    {enabled: !isNew},
  );
  const {data: starter, isLoading: starterLoading} = trpc.internal.enrollmentSite.starterFor.useQuery(
    {name: seedName || 'New site', slug: validSeedSlug, requiresAccessCode: true},
    {enabled: isNew},
  );

  const [config, setConfig] = useState<GroupConfig | null>(null);
  const [savedConfig, setSavedConfig] = useState<GroupConfig | null>(null);
  const [e123, setE123] = useState<E123Config | null>(null);
  const [savedE123, setSavedE123] = useState<E123Config | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [activeId, setActiveId] = useState('brand');
  const [discardOpen, setDiscardOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [freshOpen, setFreshOpen] = useState(false);
  const [mirrorDialog, setMirrorDialog] = useState<'start' | 'settings' | null>(null);
  const [stopMirrorOpen, setStopMirrorOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(searchParams.get('history') === '1');
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishBlockers, setPublishBlockers] = useState<string[]>([]);
  const [publishWarnings, setPublishWarnings] = useState<string[]>([]);
  const [publishChanges, setPublishChanges] = useState<ConfigChange[]>([]);
  const [publishFirstPublish, setPublishFirstPublish] = useState(false);
  const utils = trpc.useUtils();

  // Seed local + saved snapshots once. New mode seeds from the starter and
  // leaves savedConfig null (nothing persisted yet); existing mode seeds both.
  useEffect(() => {
    if (config) return;
    if (isNew && starter) {
      setConfig({...starter.siteConfig, slug: validSeedSlug, name: seedName || starter.siteConfig.name});
      setE123(starter.e123Config);
    } else if (!isNew && data) {
      setConfig(data.siteConfig);
      setSavedConfig(data.siteConfig);
      setE123(data.e123Config);
      setSavedE123(data.e123Config);
      setSavedAt(typeof data.updatedAt === 'string' ? data.updatedAt : new Date(data.updatedAt).toISOString());
    }
  }, [data, starter, isNew, config, seedName, validSeedSlug]);

  const dirty = isNew
    ? !!config
    : (!!config && !!savedConfig && JSON.stringify(config) !== JSON.stringify(savedConfig)) ||
      (!!e123 && !!savedE123 && JSON.stringify(e123) !== JSON.stringify(savedE123));

  const status = isNew ? 'DRAFT' : data?.status ?? 'DRAFT';
  // The slug is only editable before the site is published.
  const slugLocked = status === 'LIVE' || status === 'LIVE_EDITS';
  // Nothing to publish once it's live and clean; otherwise there are changes to push.
  const canPublish = !isNew && (dirty || status !== 'LIVE');
  // Revert to published only does something when there are unpublished changes to drop —
  // a saved draft ahead of live, or unsaved editor edits.
  const canRevert = !isNew && (status === 'LIVE_EDITS' || (status === 'LIVE' && dirty));
  // Version history and rollback need at least one published version.
  const canViewHistory = !isNew && status !== 'DRAFT';

  const mirror = useMemo<MirrorState | null>(
    () =>
      data?.sourceSite
        ? {
            sourceName: data.sourceSite.name,
            sourceSlug: data.sourceSite.groupSlug,
            mirroredFields: new Set(data.mirroredFields),
          }
        : null,
    [data?.sourceSite, data?.mirroredFields],
  );
  const mirroredByCount = data?.mirroredByCount ?? 0;
  const mirrorBlockedReason = isNew
    ? 'Save the site first'
    : mirroredByCount > 0
      ? 'Other sites mirror this one'
      : undefined;

  const save = trpc.internal.enrollmentSite.saveDraft.useMutation({
    onSuccess: (res, variables) => {
      setSavedConfig(variables.siteConfig as GroupConfig);
      if (variables.e123Config) setSavedE123(variables.e123Config as E123Config);
      setSavedAt(typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString());
      // Refresh derived status: a save on a live site creates unpublished edits
      // (LIVE -> LIVE_EDITS), which is what re-enables Publish. Without this the
      // stale 'LIVE' status leaves Publish disabled until a manual refresh.
      utils.internal.enrollmentSite.get.invalidate({groupSlug});
      utils.internal.enrollmentSite.list.invalidate();
      // Ping any open preview of this site to live-reload the saved draft.
      broadcastPreviewSaved(res.groupSlug ?? groupSlug);
      if (mirroredByCount > 0) {
        toast({
          title: 'Saved',
          description: `Mirrored fields also went to ${mirroredByCount} site${mirroredByCount === 1 ? '' : 's'} as unpublished drafts.`,
        });
      }
      // A slug rename moves the row to a new URL — follow it.
      if (res.groupSlug && res.groupSlug !== groupSlug) {
        navigate(`/enrollment-sites/${res.groupSlug}`, {replace: true});
      }
    },
    onError: createMutationErrorHandler('Could not save'),
  });

  const publish = trpc.internal.enrollmentSite.publish.useMutation({
    onSuccess: (res) => {
      setSavedAt(typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString());
      setPublishBlockers([]);
      setPublishWarnings([]);
      setPublishChanges([]);
      setPublishOpen(false);
      broadcastPreviewSaved(groupSlug);
      utils.internal.enrollmentSite.get.invalidate({groupSlug});
      utils.internal.enrollmentSite.list.invalidate();
      toast({title: 'Published', description: 'Your changes are now live.'});
    },
    onError: (err) => {
      // Validation blockers come back as a newline-joined BAD_REQUEST — show them in the dialog.
      if (err.data?.code === 'BAD_REQUEST' && err.message) {
        setPublishBlockers(err.message.split('\n').filter(Boolean));
        return;
      }
      createMutationErrorHandler('Could not publish')(err);
    },
  });

  // Discards unpublished edits: resets the draft (and the editor) to the published version.
  const revert = trpc.internal.enrollmentSite.revertToPublished.useMutation({
    onSuccess: (res) => {
      setConfig(res.siteConfig as GroupConfig);
      setSavedConfig(res.siteConfig as GroupConfig);
      setE123(res.e123Config as E123Config);
      setSavedE123(res.e123Config as E123Config);
      setSavedAt(typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString());
      setRevertOpen(false);
      broadcastPreviewSaved(groupSlug);
      utils.internal.enrollmentSite.get.invalidate({groupSlug});
      utils.internal.enrollmentSite.list.invalidate();
      toast({title: 'Reverted to published', description: 'Unpublished changes were discarded.'});
    },
    onError: createMutationErrorHandler('Could not revert'),
  });

  // A restored version loads into the editor as an unsaved change (savedConfig untouched),
  // so it's dirty — Save keeps it, Discard backs out, Publish rolls the live site back.
  const handleRestored = useCallback((res: {siteConfig: unknown; e123Config: unknown}) => {
    setConfig(res.siteConfig as GroupConfig);
    setE123(res.e123Config as E123Config);
  }, []);

  // New mode: the first save creates the draft row from the pending config.
  const create = trpc.internal.enrollmentSite.create.useMutation({
    onSuccess: (res, variables) => {
      // Establish the saved baseline so dirty tracking works after the new→saved switch —
      // the component doesn't remount on navigate, so the seeding effect won't re-seed it.
      setSavedConfig(variables.siteConfig as GroupConfig);
      if (variables.e123Config) setSavedE123(variables.e123Config as E123Config);
      setSavedAt(typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString());
      toast({title: 'Site created', description: `Draft "${res.groupSlug}" saved.`});
      navigate(`/enrollment-sites/${res.groupSlug}`, {replace: true});
    },
    onError: createMutationErrorHandler('Could not create site'),
  });

  // Refs keep the Cmd+S handler and guards reading current values without re-binding.
  const configRef = useRef(config);
  configRef.current = config;
  const savedRef = useRef(savedConfig);
  savedRef.current = savedConfig;
  const e123Ref = useRef(e123);
  e123Ref.current = e123;
  const savedE123Ref = useRef(savedE123);
  savedE123Ref.current = savedE123;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const handleSave = useCallback(() => {
    const cfg = configRef.current;
    if (!cfg) return;
    const e = e123Ref.current;
    if (isNew) {
      if (create.isPending) return;
      create.mutate({
        groupSlug: cfg.slug,
        name: cfg.name,
        requiresAccessCode: groupRequiresAccessCode(cfg),
        employerKey: seedEmployerKey,
        siteConfig: cfg,
        e123Config: e ?? undefined,
      });
      return;
    }
    const saved = savedRef.current;
    if (save.isPending) return;
    const savedE = savedE123Ref.current;
    const cfgClean = saved && JSON.stringify(cfg) === JSON.stringify(saved);
    const e123Clean = !e || (savedE && JSON.stringify(e) === JSON.stringify(savedE));
    if (cfgClean && e123Clean) return;
    save.mutate({groupSlug, siteConfig: cfg, e123Config: e ?? undefined, lastKnownUpdatedAt: savedAt ?? undefined});
  }, [groupSlug, save, savedAt, isNew, create, seedEmployerKey]);

  // Mirror dialog: save unsaved edits first so own-field changes survive the link.
  const saveDraftNow = useCallback(async () => {
    const cfg = configRef.current;
    if (!cfg) return savedAt ?? undefined;
    const res = await save.mutateAsync({
      groupSlug,
      siteConfig: cfg,
      e123Config: e123Ref.current ?? undefined,
      lastKnownUpdatedAt: savedAt ?? undefined,
    });
    return typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString();
  }, [groupSlug, save, savedAt]);

  const applyMirrorResult = useCallback(
    (res: {siteConfig: GroupConfig; updatedAt: string | Date}, source: {name: string}) => {
      setConfig(res.siteConfig);
      setSavedConfig(res.siteConfig);
      setSavedAt(typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString());
      const started = mirrorDialog === 'start';
      setMirrorDialog(null);
      broadcastPreviewSaved(groupSlug);
      utils.internal.enrollmentSite.get.invalidate({groupSlug});
      utils.internal.enrollmentSite.list.invalidate();
      toast(
        started
          ? {
              title: `Now mirroring ${source.name}`,
              description: `Mirrored fields are locked. Changes saved on ${source.name} arrive here as drafts.`,
            }
          : {title: 'Mirror settings updated'},
      );
    },
    [groupSlug, mirrorDialog, utils],
  );

  const stopMirroring = trpc.internal.enrollmentSite.stopMirroring.useMutation({
    onSuccess: () => {
      setStopMirrorOpen(false);
      utils.internal.enrollmentSite.get.invalidate({groupSlug});
      utils.internal.enrollmentSite.list.invalidate();
      toast({title: 'Stopped mirroring', description: 'This site keeps its content and is fully editable.'});
    },
    onError: createMutationErrorHandler('Could not stop mirroring'),
  });

  // Checks/diffs the in-memory draft — nothing is saved until the user confirms, so
  // backing out touches nothing. Reuses the same @common validators the mutation enforces.
  const handlePublish = useCallback(() => {
    if (isNew || publish.isPending) return;
    const cfg = configRef.current;
    const e = e123Ref.current;
    if (!cfg || !e) return;
    const published = data?.publishedSiteConfig ?? null;
    setPublishBlockers(collectPublishBlockers(cfg, e));
    setPublishWarnings(collectPublishWarnings(cfg, {activeLocationCount: data?.activeLocationCount ?? 0}));
    setPublishChanges(published ? diffGroupConfigs(published, cfg) : []);
    setPublishFirstPublish(!published);
    setPublishOpen(true);
  }, [isNew, publish.isPending, data?.publishedSiteConfig, data?.activeLocationCount]);

  // On confirm: save the draft (if there are unsaved edits) then publish it.
  const doPublish = useCallback(async () => {
    if (publish.isPending) return;
    let slug = groupSlug;
    let updatedAt = savedAt ?? undefined;
    if (dirtyRef.current) {
      const cfg = configRef.current;
      if (!cfg) return;
      try {
        const res = await save.mutateAsync({
          groupSlug,
          siteConfig: cfg,
          e123Config: e123Ref.current ?? undefined,
          lastKnownUpdatedAt: savedAt ?? undefined,
        });
        slug = res.groupSlug ?? groupSlug;
        updatedAt = typeof res.updatedAt === 'string' ? res.updatedAt : new Date(res.updatedAt).toISOString();
      } catch {
        return; // save error already surfaced by its own handler
      }
    }
    publish.mutate({groupSlug: slug, lastKnownUpdatedAt: updatedAt});
  }, [groupSlug, savedAt, save, publish]);

  const confirmDiscard = useCallback(() => {
    if (isNew) {
      navigate('/enrollment-sites');
      return;
    }
    if (savedRef.current) setConfig(savedRef.current);
    if (savedE123Ref.current) setE123(savedE123Ref.current);
    setDiscardOpen(false);
  }, [isNew, navigate]);

  function handleBack() {
    if (dirty) {
      setLeaveOpen(true);
      return;
    }
    navigate('/enrollment-sites');
  }

  // Cmd/Ctrl+S saves.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (canManage && dirtyRef.current) handleSave();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canManage, handleSave]);

  // Warn on refresh / tab close / hard navigation while dirty.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  function patch(updates: Partial<GroupConfig>) {
    setConfig((prev) => (prev ? {...prev, ...updates} : prev));
  }
  function patchE123(updates: Partial<E123Config>) {
    setE123((prev) => (prev ? {...prev, ...updates} : prev));
  }

  // Copy another site's content in, keeping this site's identity and dropping the
  // plan→PDID map (plans changed, and PDIDs are group-specific). Cards linking to the
  // source's own pages are pointed at this site's.
  function applyTemplate(sourceConfig: GroupConfig, sourceName: string) {
    setConfig((prev) =>
      prev
        ? {
            ...sourceConfig,
            slug: prev.slug,
            name: prev.name,
            resourcesSection: retargetSiteLinks(sourceConfig.resourcesSection, sourceConfig.slug, prev.slug),
          }
        : sourceConfig,
    );
    // The copied pay period drives the billing period, so they can't disagree.
    setE123((prev) =>
      prev ? {...prev, planPdid: {}, periodId: sitePayPeriod(sourceConfig).e123PeriodId} : prev,
    );
    setTemplateOpen(false);
    toast({
      title: 'Content copied',
      description: `Copied from ${sourceName}. Set this site’s e123 PDIDs in Submission mapping, then Save.`,
    });
  }

  // Reset the draft to a fresh starter (shared defaults, empty products/plans/e123).
  // The dialog closes itself on click; this runs after, so no overlay lingers.
  async function startFresh() {
    try {
      const fresh = await utils.internal.enrollmentSite.starter.fetch({groupSlug});
      setConfig(fresh.siteConfig);
      setE123(fresh.e123Config);
      toast({
        title: 'Reset to blank',
        description: 'Cleared to a fresh site. Save to keep it, or Discard to revert.',
      });
    } catch {
      toast({title: 'Could not reset', description: 'Something went wrong — try again.'});
    }
  }

  const loading = isNew ? starterLoading : isLoading;
  const failed = !isNew && (!!error || (!loading && !data));
  if (!failed && (loading || !config || !e123)) {
    return (
      <div className='grid min-h-[60vh] place-items-center'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }
  if (failed || !config || !e123) {
    const notFound = error?.data?.code === 'NOT_FOUND';
    return (
      <div className='p-6'>
        <div className='mx-auto grid min-h-[60vh] max-w-md place-items-center text-center'>
          <div className='space-y-4'>
            <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground'>
              {notFound ? <FileQuestion className='h-6 w-6' /> : <CloudOff className='h-6 w-6' />}
            </div>
            <div className='space-y-1'>
              <h2 className='text-lg font-semibold'>
                {notFound ? 'Site not found' : 'Couldn’t load this site'}
              </h2>
              <p className='text-sm text-muted-foreground'>
                {notFound
                  ? 'This enrollment site doesn’t exist or may have been removed.'
                  : 'We couldn’t reach the server. Check your connection and try again.'}
              </p>
            </div>
            <div className='flex items-center justify-center gap-2 pt-1'>
              <Button variant='outline' size='sm' onClick={() => navigate('/enrollment-sites')}>
                <ArrowLeft className='mr-1.5 h-4 w-4' />
                Back to sites
              </Button>
              {!notFound && (
                <Button size='sm' onClick={() => void refetch()} disabled={isFetching}>
                  {isFetching ? (
                    <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='mr-1.5 h-4 w-4' />
                  )}
                  Retry
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeIndex = Math.max(0, SECTIONS.findIndex((s) => s.id === activeId));
  const active = SECTIONS[activeIndex];
  const ActiveComponent = active.component;
  const ActiveIcon = active.icon;
  const activeMirrorState = sectionMirrorState(active.id, mirror?.mirroredFields);
  // Products locks per product itself, so plan cards stay expandable and rates can stay own.
  const lockWholeSection = activeMirrorState === 'full' && active.id !== 'products';
  const prev = SECTIONS[activeIndex - 1];
  const next = SECTIONS[activeIndex + 1];

  return (
    <div className='p-6'>
      <DetailHeader
        backLabel='Back to sites'
        onBack={handleBack}
        icon={<LayoutTemplate className='h-5 w-5' />}
        title='Enrollment Site Builder'
        subtitle={
          <>
            <span className='whitespace-nowrap'>
              Authoring <span className='font-medium text-foreground'>{config.name}</span>
            </span>
            <span>·</span>
            {!isNew && data?.liveUrl ? (
              <a href={data.liveUrl} target='_blank' rel='noopener noreferrer' title='Open the live site in a new tab'>
                <Badge variant={STATUS_VARIANT[status]} className='whitespace-nowrap font-normal'>
                  {STATUS_LABEL[status]}
                  <ExternalLink className='ml-1 h-3 w-3' />
                </Badge>
              </a>
            ) : (
              <Badge
                variant={isNew ? 'info' : STATUS_VARIANT[status]}
                className='whitespace-nowrap font-normal'
              >
                {isNew ? 'Draft · unsaved' : STATUS_LABEL[status]}
              </Badge>
            )}
            {mirror && (
              // New tab: the builder keeps its loaded site across URL changes, so an in-tab jump
              // to another site would show stale content.
              <Link
                to={`/enrollment-sites/${mirror.sourceSlug}`}
                target='_blank'
                rel='noopener noreferrer'
                title='Open the source site in a new tab'
              >
                <Badge variant='secondary' className='whitespace-nowrap font-normal'>
                  <Link2 className='mr-1 h-3 w-3' />
                  Mirroring {mirror.sourceName}
                  <ExternalLink className='ml-1 h-3 w-3' />
                </Badge>
              </Link>
            )}
            {mirroredByCount > 0 && (
              <Badge variant='secondary' className='whitespace-nowrap font-normal'>
                <Link2 className='mr-1 h-3 w-3' />
                Source for {mirroredByCount} site{mirroredByCount === 1 ? '' : 's'}
              </Badge>
            )}
          </>
        }
        actions={
          <BuilderActions
            dirty={dirty}
            saving={save.isPending || create.isPending}
            errored={save.isError}
            savedAt={savedAt}
            canManage={canManage}
            canPublish={canPublish}
            canPreview={!isNew}
            publishing={publish.isPending}
            onSave={handleSave}
            onPublish={handlePublish}
            onPreview={() => window.open(`/enrollment-sites/${groupSlug}/preview`, '_blank', 'noopener')}
            onDiscard={() => setDiscardOpen(true)}
            onCopyTemplate={() => setTemplateOpen(true)}
            onStartFresh={() => setFreshOpen(true)}
            canStartFresh={!isNew}
            mirroring={!!mirror}
            mirrorBlockedReason={mirrorBlockedReason}
            mirrorSourceName={mirror?.sourceName}
            onMirror={() => setMirrorDialog('start')}
            onMirrorSettings={() => setMirrorDialog('settings')}
            onStopMirroring={() => setStopMirrorOpen(true)}
            canRevert={canRevert}
            onRevert={() => setRevertOpen(true)}
            canViewHistory={canViewHistory}
            onHistory={() => setHistoryOpen(true)}
          />
        }
      />

      {/* Body */}
      <div className='flex gap-3 lg:gap-6'>
        {/* Journey rail — full labels at lg+, collapses to a numbered column below that. */}
        <div className='w-10 shrink-0 space-y-4 self-start lg:w-64'>
          <nav className='space-y-1.5 lg:space-y-0.5 lg:rounded-xl lg:border lg:bg-white lg:p-3 lg:shadow-[0_1px_3px_rgba(16,24,40,0.07)]'>
            <div className='hidden px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground lg:block'>
              Enrollee journey
            </div>
            <div className='space-y-1.5 lg:space-y-0.5'>
              {SECTIONS.map((s, idx) => {
                const isActive = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveId(s.id)}
                    title={s.label}
                    className={cn(
                      'group flex w-full items-center gap-3 py-1 text-left transition-colors',
                      'justify-center lg:justify-start lg:items-start lg:rounded-lg lg:px-2.5 lg:py-2',
                      isActive ? 'lg:bg-primary/5 lg:ring-1 lg:ring-primary/20' : 'lg:hover:bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors lg:mt-0.5 lg:h-6 lg:w-6',
                        isActive
                          ? 'border-primary bg-primary text-primary-foreground shadow-[0_1px_3px_rgba(16,24,40,0.18)] lg:shadow-none'
                          : 'border-border bg-white text-muted-foreground group-hover:border-primary/40 group-hover:text-foreground',
                      )}
                    >
                      {idx + 1}
                    </span>
                    <span className='hidden min-w-0 lg:block'>
                      <span
                        className={cn('flex items-center gap-1.5 text-sm', isActive ? 'font-semibold' : 'font-medium')}
                      >
                        {s.label}
                        {sectionMirrorState(s.id, mirror?.mirroredFields) !== 'none' && (
                          <Link2
                            className='h-3 w-3 shrink-0 text-muted-foreground'
                            aria-label={
                              sectionMirrorState(s.id, mirror?.mirroredFields) === 'full'
                                ? `Mirrored from ${mirror?.sourceName}`
                                : `Partly mirrored from ${mirror?.sourceName}`
                            }
                          />
                        )}
                      </span>
                      <span className='block text-xs text-muted-foreground'>{s.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
          <div className='hidden rounded-xl border bg-white p-3 shadow-[0_1px_3px_rgba(16,24,40,0.07)] lg:block'>
            <div className='px-0.5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>
              Access codes
            </div>
            <Button
              variant='outline'
              size='sm'
              className='w-full'
              disabled={!groupRequiresAccessCode(config) || isNew}
              onClick={() => navigate(`/enrollment-sites/${groupSlug}/locations`)}
            >
              <MapPin className='mr-1.5 h-4 w-4' />
              Manage access codes
            </Button>
            {!groupRequiresAccessCode(config) ? (
              <p className='mt-1.5 text-xs text-muted-foreground'>
                This site is public — it doesn’t use access codes.
              </p>
            ) : isNew ? (
              <p className='mt-1.5 text-xs text-muted-foreground'>
                Save the site first to add access codes.
              </p>
            ) : null}
          </div>
        </div>

        {/* Active section card */}
        <div className='min-w-0 flex-1'>
          <div className={cn('rounded-xl border bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,0.07)]', !canManage && 'pointer-events-none opacity-60')}>
            <header className='mb-6 flex items-start gap-3 border-b pb-5'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
                <ActiveIcon className='h-5 w-5' />
              </div>
              <div>
                <div className='flex items-center gap-2'>
                  <h2 className='text-lg font-semibold'>{active.label}</h2>
                  <span className='rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground'>
                    {active.tag}
                  </span>
                </div>
                <p className='text-sm text-muted-foreground'>{active.description}</p>
              </div>
            </header>

            {mirror && activeMirrorState === 'full' && (
              <div className='mb-5 flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground'>
                <Link2 className='mt-0.5 h-4 w-4 shrink-0' />
                <p>
                  This section follows{' '}
                  <Link
                    to={`/enrollment-sites/${mirror.sourceSlug}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-0.5 font-medium text-foreground underline'
                  >
                    {mirror.sourceName}
                    <ExternalLink className='h-3 w-3' />
                  </Link>
                  . Edit it there; changes arrive here as a draft.
                </p>
              </div>
            )}
            {ActiveComponent ? (
              <MirrorProvider value={{mirror, sectionLocked: lockWholeSection}}>
                <fieldset
                  disabled={lockWholeSection}
                  className={cn('m-0 min-w-0 border-0 p-0', lockWholeSection && 'opacity-60')}
                >
                  <ActiveComponent
                    config={config}
                    patch={patch}
                    e123={e123}
                    patchE123={patchE123}
                    slugLocked={slugLocked}
                    groupSlug={groupSlug}
                    isNew={isNew}
                  />
                </fieldset>
              </MirrorProvider>
            ) : (
              <div className='rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground'>
                {active.placeholder}
              </div>
            )}

            <footer className='mt-8 flex items-center justify-between border-t pt-5'>
              {prev ? (
                <Button variant='ghost' size='sm' onClick={() => setActiveId(prev.id)}>
                  <ArrowLeft className='mr-1.5 h-4 w-4' />
                  {prev.label}
                </Button>
              ) : (
                <span />
              )}
              {next && (
                <Button variant='outline' size='sm' onClick={() => setActiveId(next.id)}>
                  {next.label}
                  <ArrowRight className='ml-1.5 h-4 w-4' />
                </Button>
              )}
            </footer>
          </div>
        </div>
      </div>

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              This reverts the site to its last saved version. Any edits you haven’t saved will be
              lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDiscard();
              }}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {templateOpen && (
        <TemplatePickerDialog
          currentSlug={groupSlug}
          dirty={dirty}
          onApply={applyTemplate}
          onClose={() => setTemplateOpen(false)}
        />
      )}

      {mirrorDialog && (
        <MirrorDialog
          mode={mirrorDialog}
          groupSlug={groupSlug}
          siteConfig={config}
          onSetPayPeriod={(period) => {
            patch({payPeriod: period === 'weekly' ? undefined : period});
            patchE123({periodId: PAY_PERIODS[period].e123PeriodId});
          }}
          sections={SECTIONS}
          source={mirror ? {groupSlug: mirror.sourceSlug, name: mirror.sourceName} : undefined}
          initialFields={mirrorDialog === 'settings' ? data?.mirroredFields : undefined}
          dirty={dirty}
          saveFirst={saveDraftNow}
          lastKnownUpdatedAt={savedAt ?? undefined}
          onApplied={applyMirrorResult}
          onClose={() => setMirrorDialog(null)}
        />
      )}

      <AlertDialog open={stopMirrorOpen} onOpenChange={setStopMirrorOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop mirroring {mirror?.sourceName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This site keeps its current content and becomes fully editable. Future changes to{' '}
              {mirror?.sourceName} won’t reach it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={stopMirroring.isPending}>Keep mirroring</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                stopMirroring.mutate({groupSlug});
              }}
              disabled={stopMirroring.isPending}
            >
              {stopMirroring.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
              Stop mirroring
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={freshOpen} onOpenChange={setFreshOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start blank?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the current draft to a blank site, including any unsaved changes. After you
              reset, Save to keep it, or Discard to revert to the last saved copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void startFresh()}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              Start blank
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={revertOpen} onOpenChange={setRevertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to the published version?</AlertDialogTitle>
            <AlertDialogDescription>
              This discards every change since the last publish — the draft goes back to what’s live
              at <span className='font-medium'>/{groupSlug}</span>. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                revert.mutate({groupSlug, lastKnownUpdatedAt: savedAt ?? undefined});
              }}
              disabled={revert.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              Revert to published
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!isNew && (
        <VersionHistoryDialog
          open={historyOpen}
          onOpenChange={(open) => {
            setHistoryOpen(open);
            // Drop ?history=1 on close so a refresh or shared link doesn't reopen it.
            if (!open && searchParams.get('history')) {
              const next = new URLSearchParams(searchParams);
              next.delete('history');
              setSearchParams(next, {replace: true});
            }
          }}
          groupSlug={groupSlug}
          canManage={canManage}
          dirty={dirty}
          onRestored={handleRestored}
        />
      )}

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Leaving now discards them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setLeaveOpen(false);
                navigate('/enrollment-sites');
              }}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={publishOpen}
        onOpenChange={(open) => {
          setPublishOpen(open);
          if (!open) {
            setPublishBlockers([]);
            setPublishWarnings([]);
            setPublishChanges([]);
          }
        }}
      >
        <AlertDialogContent className='sm:max-w-2xl'>
          {publishBlockers.length > 0 ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Can’t publish yet</AlertDialogTitle>
                <AlertDialogDescription>
                  Fix these before this site can go live:
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ul className='space-y-1.5 text-sm'>
                {publishBlockers.map((b, i) => (
                  <li key={i} className='flex gap-2'>
                    <AlertCircle className='mt-0.5 h-4 w-4 shrink-0 text-destructive' />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <AlertDialogFooter>
                <AlertDialogCancel>Close</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Publish this site?</AlertDialogTitle>
                <AlertDialogDescription>
                  This makes the current draft live at{' '}
                  <span className='font-medium'>/{groupSlug}</span> and saves a new version.
                </AlertDialogDescription>
              </AlertDialogHeader>

              {dirty && (
                <div className='flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800'>
                  <Info className='mt-0.5 h-4 w-4 shrink-0' />
                  <span>
                    You have unsaved changes — they’ll be <span className='font-medium'>saved and
                    published</span>, and are included below.
                  </span>
                </div>
              )}

              <div className='space-y-1.5'>
                <div className='flex items-center justify-between'>
                  <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                    Review changes
                  </p>
                  {!publishFirstPublish && publishChanges.length > 0 && (
                    <span className='text-xs text-muted-foreground'>
                      {publishChanges.length} change{publishChanges.length === 1 ? '' : 's'} from live
                    </span>
                  )}
                </div>
                {publishFirstPublish ? (
                  <p className='rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground'>
                    First publish — your entire site goes live.
                  </p>
                ) : publishChanges.length === 0 ? (
                  <p className='rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground'>
                    No content changes since the last published version.
                  </p>
                ) : (
                  <ConfigDiffList changes={publishChanges} />
                )}
              </div>

              {publishWarnings.length > 0 && (
                <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
                  <p className='flex items-center gap-1.5 text-sm font-medium text-amber-800'>
                    <AlertTriangle className='h-4 w-4' />
                    Recommended before sharing
                  </p>
                  <ul className='mt-2 space-y-1 text-sm text-amber-800'>
                    {publishWarnings.map((w, i) => (
                      <li key={i} className='flex gap-2'>
                        <span className='mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500' />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                  <p className='mt-2 text-xs text-amber-700'>
                    You can publish now and address these later.
                  </p>
                </div>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void doPublish();
                  }}
                  disabled={publish.isPending || save.isPending}
                >
                  {publish.isPending || save.isPending ? (
                    <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                  ) : (
                    <Rocket className='mr-1.5 h-4 w-4' />
                  )}
                  Publish
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface BuilderActionsProps {
  dirty: boolean;
  saving: boolean;
  errored: boolean;
  savedAt: string | null;
  canManage: boolean;
  canPublish: boolean;
  canPreview: boolean;
  publishing: boolean;
  onSave: () => void;
  onPublish: () => void;
  onPreview: () => void;
  onDiscard: () => void;
  onCopyTemplate: () => void;
  onStartFresh: () => void;
  canStartFresh: boolean;
  mirroring: boolean;
  mirrorBlockedReason?: string;
  mirrorSourceName?: string;
  onMirror: () => void;
  onMirrorSettings: () => void;
  onStopMirroring: () => void;
  canRevert: boolean;
  onRevert: () => void;
  canViewHistory: boolean;
  onHistory: () => void;
}

// Header CTA group: button row with a save-status line beneath it. Preview is a
// visual placeholder (no handler yet); Template, Save, Discard, and Publish are wired.
function BuilderActions({dirty, saving, errored, savedAt, canManage, canPublish, canPreview, publishing, onSave, onPublish, onPreview, onDiscard, onCopyTemplate, onStartFresh, canStartFresh, mirroring, mirrorBlockedReason, mirrorSourceName, onMirror, onMirrorSettings, onStopMirroring, canRevert, onRevert, canViewHistory, onHistory}: BuilderActionsProps) {
  // Copy and Start blank would overwrite mirrored content.
  const canCopy = !mirroring;
  const canBlank = canStartFresh && !mirroring;
  return (
    <div className='flex flex-col items-end gap-1.5'>
      <div className='flex flex-wrap items-center justify-end gap-2'>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant='outline' size='sm' className='bg-white' disabled={!canManage}>
              <Wrench className='mr-1.5 h-4 w-4' />
              Actions
              <ChevronDown className='ml-1 h-3.5 w-3.5' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-72'>
            <DropdownMenuItem
              onSelect={onCopyTemplate}
              disabled={!canCopy}
              className={cn('gap-2.5 px-3 py-2.5', canCopy ? 'cursor-pointer' : 'opacity-50')}
            >
              <Copy className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
              <div className='flex flex-col gap-0.5'>
                <span className='text-sm'>Copy from another site</span>
                <span className='text-xs text-muted-foreground'>
                  {mirroring ? 'Turned off while mirroring' : 'Start from an existing site’s content'}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onStartFresh}
              disabled={!canBlank}
              className={cn('gap-2.5 px-3 py-2.5', canBlank && 'cursor-pointer')}
            >
              <FilePlus className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
              <div className='flex flex-col gap-0.5'>
                <span className='text-sm'>Start blank</span>
                <span className='text-xs text-muted-foreground'>
                  {mirroring ? 'Turned off while mirroring' : 'Clear everything and begin from scratch'}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {mirroring ? (
              <>
                <DropdownMenuItem onSelect={onMirrorSettings} className='cursor-pointer gap-2.5 px-3 py-2.5'>
                  <Link2 className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                  <div className='flex flex-col gap-0.5'>
                    <span className='text-sm'>Mirror settings</span>
                    <span className='text-xs text-muted-foreground'>Choose which fields follow {mirrorSourceName}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onStopMirroring} className='cursor-pointer gap-2.5 px-3 py-2.5'>
                  <Unlink className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                  <div className='flex flex-col gap-0.5'>
                    <span className='text-sm'>Stop mirroring</span>
                    <span className='text-xs text-muted-foreground'>Keep the current content and edit it freely</span>
                  </div>
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onSelect={onMirror}
                disabled={!!mirrorBlockedReason}
                className={cn('gap-2.5 px-3 py-2.5', mirrorBlockedReason ? 'opacity-50' : 'cursor-pointer')}
              >
                <Link2 className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                <div className='flex flex-col gap-0.5'>
                  <span className='text-sm'>Mirror another site</span>
                  <span className='text-xs text-muted-foreground'>
                    {mirrorBlockedReason ?? 'Follow another site’s content and stay in sync'}
                  </span>
                </div>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onRevert}
              disabled={!canRevert}
              className={cn('gap-2.5 px-3 py-2.5', canRevert ? 'cursor-pointer' : 'opacity-50')}
            >
              <RotateCcw className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
              <div className='flex flex-col gap-0.5'>
                <span className='text-sm'>Revert to published</span>
                <span className='text-xs text-muted-foreground'>
                  Discard unpublished edits and match the live site
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onHistory}
              disabled={!canViewHistory}
              className={cn('gap-2.5 px-3 py-2.5', canViewHistory ? 'cursor-pointer' : 'opacity-50')}
            >
              <History className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
              <div className='flex flex-col gap-0.5'>
                <span className='text-sm'>Version history</span>
                <span className='text-xs text-muted-foreground'>
                  Browse published versions and roll back
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant='outline'
          size='sm'
          className='bg-white'
          onClick={onPreview}
          disabled={!canPreview}
          title={canPreview ? 'Open a preview of the saved draft' : 'Save the site first'}
        >
          <Eye className='mr-1.5 h-4 w-4' />
          Preview
        </Button>
        {/* Discard lives in a fixed slot next to Save — muted when clean, red when dirty, so it never shifts the row. */}
        <Button
          variant='outline'
          size='sm'
          className={cn('bg-white', canManage && dirty && !saving && 'text-destructive hover:text-destructive')}
          onClick={onDiscard}
          disabled={!canManage || !dirty || saving}
          title='Discard changes'
        >
          <RotateCcw className='mr-1.5 h-4 w-4' />
          Discard
        </Button>
        <Button
          variant='outline'
          size='sm'
          className='bg-white'
          onClick={onSave}
          disabled={!canManage || !dirty || saving}
        >
          {saving ? <Loader2 className='mr-1.5 h-4 w-4 animate-spin' /> : <SaveIcon className='mr-1.5 h-4 w-4' />}
          Save
        </Button>
        <Button
          size='sm'
          onClick={onPublish}
          disabled={!canManage || !canPublish || publishing || saving}
        >
          {publishing ? <Loader2 className='mr-1.5 h-4 w-4 animate-spin' /> : <Rocket className='mr-1.5 h-4 w-4' />}
          Publish
        </Button>
      </div>
      {saving ? (
        <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
          <Loader2 className='h-3 w-3 animate-spin' /> Saving...
        </span>
      ) : errored ? (
        <span className='flex items-center gap-1.5 text-xs text-destructive'>
          <AlertCircle className='h-3 w-3' /> Couldn’t save — try again
        </span>
      ) : dirty ? (
        <span className='flex items-center gap-1.5 text-xs text-amber-600'>
          <span className='h-1.5 w-1.5 rounded-full bg-amber-500' /> Unsaved changes
        </span>
      ) : (
        <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
          <Check className='h-3 w-3 text-green-600' /> {formatSavedAt(savedAt)}
        </span>
      )}
    </div>
  );
}
